import type { PropsWithChildren } from 'react';
import React from 'react';
import cx from 'clsx';

import styles from './Descriptions.module.css';

type DescriptionAlignment = 'left' | 'right';
type DescriptionDirection = 'row' | 'column';
type DescriptionsCaptionsProps = {
  label?: string;
};

const DescriptionCaption = ({
  label,
  children,
}: PropsWithChildren<DescriptionsCaptionsProps>) => {
  return (
    <div className="flex justify-between text-hint typo-caption100-regular">
      <span>{label}</span>
      <span>{children}</span>
    </div>
  );
};

type DescriptionsItemProps = {
  label?: string | React.ReactNode;
  alignment?: DescriptionAlignment;
  direction?: DescriptionDirection;
  highlight?: boolean;
  bold?: boolean;
  minWidth?: number;
  description?: string | React.ReactNode;
  danger?: boolean;
};
const DescriptionsItem = ({
  children,
  label,
  highlight,
  bold,
  description,
  danger,
  alignment = 'left',
  direction = 'row',
  minWidth = 76,
}: PropsWithChildren<DescriptionsItemProps>) => {
  return (
    <div className={cx([styles.item, styles[direction as string]])}>
      <div
        className={cx([
          styles.label,
          bold && styles.alignCenter,
          highlight && styles.highlight,
          danger && styles.danger,
        ])}
        style={{ minWidth: `${minWidth}px` }}
      >
        {label}
        {description && (
          <span className={styles.description}>{description}</span>
        )}
      </div>
      <div
        className={cx([
          styles.text,
          bold && styles.bold,
          danger && styles.danger,
          styles[alignment as string],
        ])}
      >
        {children}
      </div>
    </div>
  );
};

type DescriptionsProps = {
  title?: string;
  alignment?: DescriptionAlignment;
  direction?: DescriptionDirection;
  labelMinWidth?: number;
};
const Descriptions = ({
  children,
  title,
  alignment,
  direction,
  labelMinWidth,
}: PropsWithChildren<DescriptionsProps>) => {
  return (
    <>
      {title && <h3 className={styles.title}>{title}</h3>}
      <div>
        {React.Children.map(children, (child) => {
          const item = child as
            | React.ReactElement<React.PropsWithChildren<DescriptionsItemProps>>
            | null
            | undefined
            | false;

          if (!item) {
            return null;
          }

          return React.cloneElement(item, {
            ...item.props,
            alignment,
            direction,
            minWidth: labelMinWidth,
          });
        })}
      </div>
    </>
  );
};

type DescriptionsType = typeof Descriptions & {
  Item: typeof DescriptionsItem;
  Caption: typeof DescriptionCaption;
};

(Descriptions as DescriptionsType).Item = DescriptionsItem;
(Descriptions as DescriptionsType).Caption = DescriptionCaption;

export default Descriptions as DescriptionsType;
